import { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';

import {
  Loader, Checkbox, Select, NewTable,
} from 'components';
import { HealthIcon, healthLevelDesc } from 'components/HealthIcon';
import { useStateVar } from 'helpers/useStateVar';
import { apiCall, apiCallDownload } from 'providers';

import { t } from 'i18next';

import { AdminLayout } from '../AdminLayout';
import {
  SelectContainer,
  LoaderContainer,
  Scroll,
  FaultsRow,
  StyledLink,
  TransparentLink,
  Container,
  ButtonExport,
} from './styles';
import { FaultsLayout } from '../FaultsLayout';
import { FaultState } from '../../../providers/types/api-private';
import { withTransaction } from '@elastic/apm-rum-react';
import { useLocation } from 'react-router-dom';
import { FilterFaults } from '~/components/FilterFaults';
import { LayoutIcon } from '@radix-ui/react-icons';
import { Moment } from 'moment-timezone';
import { AnalysisEmpty } from '~/pages/Analysis/AnalysisEmpty';

type Fault = {
  id: string;
  origem: string;
  desc: string;
  primDet: string;
  ultDet: string;
  faultState: FaultState;
  faultStateDesc: string;
  faultLevel: number;
  checked?: boolean;
}

type Dev = {
  DEV_ID: string;
  CLIENT_NAME: string;
  UNIT_NAME: string;
  UNIT_ID: number;
  H_INDEX: number;
  H_DESC: string;
  hLevelDesc: string;
  faults: Fault[];
};

const APPROVAL_DESCS = {
  APPROVED: t('estadoDaFalhaOcorrenciaRatificada'),
  PENDING: t('estadoDaFalhaOcorrenciaPendente'),
  RESTABLISHED: t('estadoDaFalhaRestabelecimentoRatificado'),
  RESTAB_PENDING: t('estadoDaFalhaRestabelecimentoPendente'),
};

export function FaultsList(): JSX.Element {
  const [state, render, setState] = useStateVar({
    loading: false,
    selectPositionX: 223,
    selectPositionY: 1536,
    selectedClientOpts: [] as number[],
    selectedUnitOpts: [] as number[],
    selectedDevices: [] as string[],
    selectedMachinesIds: [] as string[],
    selectedHealthIndexes: [] as number[],
    selectedCurrentHealthCriticality: [] as string[],
    selectedFaultsDetected: [] as string[],
    onlyFaultsDetectedInAllCycles: false as boolean,
    selectedFaultCriticality: [] as number[],
    selectedDateStartFirstAlert: null as Moment | null,
    selectedDateEndFirstAlert: null as Moment | null,
    selectedDateStartLastAlert: null as Moment | null,
    selectedDateEndLastAlert: null as Moment | null,
    selectedFaultStates: [] as FaultState[],
    devs: [] as Dev[],
    filteredClick: false as boolean,
  });
  const { pathname } = useLocation();

  const faultType: 'dac'|'dut'| undefined = useMemo(() => {
    const splitPath = pathname.split('/');
    const faultType = splitPath[splitPath.length - 1];
    if (faultType === 'dac' || faultType === 'dut') {
      return faultType;
    }
  }, [pathname]);

  async function loadData() {
    setState({ loading: true });
    try {
      const { list: faultyDacs } = await apiCall('/dev/get-faults', {
        devType: faultType,
        clientIds: state.selectedClientOpts,
        unitIds: state.selectedUnitOpts,
        machinesIds: state.selectedMachinesIds?.map((item) => item.split(',').map(Number)).flat(),
        devicesIds: state.selectedDevices,
        healthsIndexes: state.selectedHealthIndexes,
        currentHealthCriticality: state.selectedCurrentHealthCriticality,
        faultsDetected: state.selectedFaultsDetected,
        onlyFaultsDetectedInAllCycles: state.onlyFaultsDetectedInAllCycles,
        detectedFaultCriticality: state.selectedFaultCriticality,
        firstAlertFrom: state.selectedDateStartFirstAlert ? state.selectedDateStartFirstAlert.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        firstAlertTo: state.selectedDateEndFirstAlert ? state.selectedDateEndFirstAlert.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        lastAlertFrom: state.selectedDateStartLastAlert ? state.selectedDateStartLastAlert.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        lastAlertTo: state.selectedDateEndLastAlert ? state.selectedDateEndLastAlert.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        faultStates: state.selectedFaultStates,
      });

      state.devs = [];
      faultyDacs.sort((
        { CLIENT_NAME: firstClient, UNIT_NAME: firstUnit, GROUP_NAME: firstMachine },
        { CLIENT_NAME: secondClient, UNIT_NAME: secondUnit, GROUP_NAME: secondMachine },
      ) => {
        if (firstClient > secondClient) {
          return 1;
        }
        if (firstClient < secondClient) {
          return -1;
        }
        if (firstUnit > secondUnit) {
          return 1;
        }
        if (firstUnit < secondUnit) {
          return -1;
        }
        if (firstMachine > secondMachine) {
          return 1;
        }
        if (firstMachine < secondMachine) {
          return -1;
        }
        return 0;
      });

      setState({ filteredClick: true });

      for (const item of faultyDacs) {
        const faults: Fault[] = [];

        if (item.fdetected) {
          Object.values(item.fdetected).forEach((falha) => {
            faults.push(buildFaultLine(falha));
          });
        }
        const hLevelDesc = healthLevelDesc[String(item.H_INDEX)];
        state.devs.push(Object.assign(item, { faults, hLevelDesc, rowkey: item.DEV_ID }));
      }
    } catch (err) {
      console.log(err);
    }
    setState({ loading: false });
  }

  const updateCheckInformations = (topic: string, newValues?: [], dates?: Moment | null, option?: boolean) => {
    switch (topic) {
      case 'client':
        setState({ selectedClientOpts: newValues });
        break;
      case 'unit':
        setState({ selectedUnitOpts: newValues });
        break;
      case 'machines':
        setState({ selectedMachinesIds: newValues });
        break;
      case 'devices':
        setState({ selectedDevices: newValues });
        break;
      case 'health':
        setState({ selectedHealthIndexes: newValues });
        break;
      case 'currentHealthCriticality':
        setState({ selectedCurrentHealthCriticality: newValues });
        break;
      case 'faultsDetected':
        setState({ selectedFaultsDetected: newValues });
        break;
      case 'onlyFaultsDetectedInAllCycles':
        setState({ onlyFaultsDetectedInAllCycles: option });
        break;
      case 'detectedFaultCriticality':
        setState({ selectedFaultCriticality: newValues });
        break;
      case 'dateStartFirstAlert':
        setState({ selectedDateStartFirstAlert: dates });
        break;
      case 'dateEndFirstAlert':
        setState({ selectedDateEndFirstAlert: dates });
        break;
      case 'dateStartLastAlert':
        setState({ selectedDateStartLastAlert: dates });
        break;
      case 'dateEndLastAlert':
        setState({ selectedDateEndLastAlert: dates });
        break;
      case 'faultStates':
        setState({ selectedFaultStates: newValues });
        break;
      default:
        break;
    }

    render();
  };

  function buildFaultLine(
    falha: {
        id: string;
        origin: string;
        faultName: string;
        faultLevel: number;
        faultStateTime: number;
        faultState: FaultState;
        lastDet: number;
        lastRiseTS: string;
        firstRiseTS: string;
    },
  ) {
    const desc = falha.faultName || '-';
    const faultState: FaultState = falha.faultState || '-' as FaultState;
    return {
      id: falha.id,
      origem: falha.origin || '-',
      desc,
      primDet: (falha?.firstRiseTS?.substring(0, 19)?.replace('T', ' ')) || '-',
      ultDet: (falha?.lastRiseTS?.substring(0, 19)?.replace('T', ' ')) || '-',
      faultStateDesc: APPROVAL_DESCS[falha.faultState],
      faultState,
      faultLevel: falha.faultLevel,
    };
  }

  function handleFaultItemClick(event, fault: { checked?: boolean }) {
    state.selectPositionX = event.clientX - 300;
    state.selectPositionY = event.clientY;
    fault.checked = !fault.checked;
    render();
  }

  const TABLE_COLUMNS = [
    {
      name: 'client',
      value: t('cliente'),
      accessor: 'CLIENT_NAME',
    },
    {
      name: 'unit',
      value: t('unidade'),
      accessor: 'UNIT_NAME',
      render: (props: Dev) => ((props.UNIT_NAME) ? <StyledLink to={`/analise/unidades/${props.UNIT_ID}`}>{props.UNIT_NAME || '-'}</StyledLink> : (props.UNIT_NAME || '-')),
    },
    {
      name: 'machines',
      value: t('maquina'),
      accessor: 'GROUP_NAME',
      render: (props) => {
        const text = props.GROUP_NAME;
        const display = text.length > 25 ? `${text.substring(0, 22)}...` : text;

        return (
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            title={t('cliqueParaCopiar')}
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(text);
            }}
          >
            {display}
          </span>
        );
      },
    },
    {
      name: 'dev',
      value: t('dispositivo'),
      accessor: 'DEV_ID',
      render: ({ DEV_ID }: Dev) => <StyledLink to={`/analise/dispositivo/${DEV_ID}/saude`}>{DEV_ID}</StyledLink>,
    },
    {
      name: 'health_now',
      value: t('saudeAtual'),
      accessor: 'H_INDEX',
      render: ({ H_DESC }: Dev) => (
        <FaultsRow>
          {H_DESC}
        </FaultsRow>
      ),
    },
    {
      name: 'current_health_criticality',
      value: t('criticidadeDaSaudeAtual'),
      sortable: false,
      accessor: 'H_DESC',
      render: ({ H_INDEX }: Dev) => <HealthIcon health={(H_INDEX || 0).toString()} label={healthLevelDesc[String(H_INDEX)]} />,
    },
    {
      name: 'fault',
      value: t('falhasDetectadas'),
      sortable: false,
      accessor: 'fault.desc',
      render: ({ faults }: Dev) => (
        <FaultsRow>
          {faults.map((fault) => <div key={fault.id}>{fault.desc}</div>)}
        </FaultsRow>
      ),
    },
    {
      name: 'detected_fault_criticality',
      value: t('criticidadeDaFalhaDetectada'),
      sortable: false,
      accessor: 'fault.faultLevel',
      render: ({ faults }: Dev) => faults.map((fault) => (<HealthIcon key={fault.id} health={(fault.faultLevel || 0).toString()} label={healthLevelDesc[String(fault.faultLevel)]} />)),
    },
    {
      name: 'first_alert',
      value: t('1oAlerta'),
      sortable: false,
      accessor: 'fault.primDet',
      render: ({ faults }: Dev) => (
        <FaultsRow style={{ height: `${faults.length * 28}px` }}>
          {faults.map((fault) => <div key={fault.id}>{fault.primDet}</div>)}
        </FaultsRow>
      ),
    },
    {
      name: 'last_alert',
      value: t('ultimaDet'),
      sortable: false,
      accessor: 'fault.ultDet',
      render: ({ faults, DEV_ID }: Dev) => (
        <TransparentLink to={`/analise/dispositivo/${DEV_ID}/historico`}>
          <FaultsRow style={{ height: `${faults.length * 28}px` }}>
            {faults.map((fault) => <div key={fault.id}>{fault.ultDet}</div>)}
          </FaultsRow>
        </TransparentLink>
      ),
    },
    {
      name: 'status',
      value: 'Estado da Falha',
      sortable: false,
      accessor: 'fault.faultState',
      render: ({ faults }: Dev) => (
        <FaultsRow style={{ height: `${faults.length * 28}px` }}>
          {faults.map((fault) => <div key={fault.id}>{APPROVAL_DESCS[fault.faultState] || fault.faultState}</div>)}
        </FaultsRow>
      ),
    },
    {
      name: 'checkbox',
      value: '',
      sortable: false,
      checkable: true,
      checkValue: [{ value: 'faults', type: 'array' }, { value: 'checked', type: 'check' }],
      width: 50,
      render: ({ faults, DEV_ID }: Dev) => (
        <FaultsRow style={{ height: `${faults.length * 28}px` }}>
          {faults.map((fault) => (
            <Checkbox
              key={fault.id}
              checked={fault.checked || false}
              onClick={(event) => handleFaultItemClick(event, fault)}
            />
          ))}
        </FaultsRow>
      ),
    },
  ];

  const selectedDacFaults = [] as { devId: string, faultId: string, state: string }[];
  state.devs.forEach((dev) => {
    dev.faults.forEach((fault) => {
      if (fault.checked) {
        selectedDacFaults.push({ devId: dev.DEV_ID, faultId: fault.id, state: fault.faultState as FaultStateBeforeApproval });
      }
    });
  });

  async function exportFaults() {
    toast.success(t('mensagemDeExportacaoIniciada'));
    try {
      const resp = await apiCallDownload('/export-assets-faults', {
        devType: faultType,
        clientIds: state.selectedClientOpts,
        unitIds: state.selectedUnitOpts,
        devicesIds: state.selectedDevices,
        machinesIds: state.selectedMachinesIds?.map((item) => item.split(',').map(Number)).flat(),
        healthsIndexes: state.selectedHealthIndexes,
        currentHealthCriticality: state.selectedCurrentHealthCriticality,
        faultsDetected: state.selectedFaultsDetected,
        onlyFaultsDetectedInAllCycles: state.onlyFaultsDetectedInAllCycles,
        detectedFaultCriticality: state.selectedFaultCriticality,
        firstAlertFrom: state.selectedDateStartFirstAlert ? state.selectedDateStartFirstAlert.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        firstAlertTo: state.selectedDateEndFirstAlert ? state.selectedDateEndFirstAlert.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        lastAlertFrom: state.selectedDateStartLastAlert ? state.selectedDateStartLastAlert.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        lastAlertTo: state.selectedDateEndLastAlert ? state.selectedDateEndLastAlert.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        faultStates: state.selectedFaultStates,
      });
      if (resp.data.type === 'application/json') {
        const msg = await resp.data.text();
        throw new Error(msg);
      }

      const cd = resp.headers['content-disposition'];
      const fallback = `${t('falhas')}.xlsx`;
      const filenameHdr = resp.headers.filename as string | undefined;
      const filename = filenameHdr ?? fallback;

      const blob = resp.data;
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      toast.success(t('sucessoExportacao'));
    } catch (err) {
      console.error(err);
      toast.error(t('erroAoExportar'));
    }
  }

  return (
    <Container>
      <Helmet>
        <title>
          Diel Energia -
          {` ${faultType === 'dut' ? t('dutsComFalha') : t('dacsComFalha')}`}
        </title>
      </Helmet>
      <AdminLayout />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '23px' }}>
        <FaultsLayout />
      </div>
      <FilterFaults
        state={state}
        onAply={() => loadData()}
        deviceType={faultType}
        updateInfos={updateCheckInformations}
        listFilters={['cliente', 'unidade', 'saude', 'deviceId', 'criticidadeAtual', 'falhasDetectadas', 'criticidadeFalhaDetectada', 'primeiroAlertaDe', 'primeiroAlertaAte', 'ultimaDeteccaoDe', 'ultimaDeteccaoAte', 'status', 'machines']}
      />
      {state.loading ? (
        <LoaderContainer>
          <Loader variant="primary" />
          <span>Carregando</span>
        </LoaderContainer>
      ) : (
        <>
          <br />

          {
            state.filteredClick === true ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <h3>
                    {state.devs.length}
                    {' '}
                    {t('falhasEncontradas')}
                  </h3>
                  <ButtonExport onClick={exportFaults}>
                    <LayoutIcon style={{ marginRight: '5px' }} />
                    {t('exportarPlanilha')}
                  </ButtonExport>
                </div>
                <Scroll style={{ overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap' }}>
                  <NewTable
                    noSearchBar
                    data={state.devs}
                    columns={TABLE_COLUMNS}
                    pageSize={20}
                    styledPagination
                  />
                </Scroll>
              </>
            ) : <AnalysisEmpty />
          }
          {(selectedDacFaults.length > 0) && (
          <SelectContainer style={{ width: '280px', top: state.selectPositionY, left: state.selectPositionX }}>
            <Select
              options={[{ label: 'Aprovar', value: 'approve' }, { label: 'Reprovar', value: 'reject' }]}
              value={null}
              placeholder={`${selectedDacFaults.length} selecionada(s)`}
              onSelect={(opt) => { approveOrRejectSelectedPendingFaults(opt.value as ApprovalAction, selectedDacFaults); }}
            />
          </SelectContainer>
          )}
        </>
      )}
    </Container>
  );
}

type ApprovalAction = 'approve' | 'reject';
type FaultStateBeforeApproval = 'PENDING' | 'RESTAB_PENDING';
type FaultStateAfterApproval = 'APPROVED' | 'RESTABLISHED';

const faultApprovalMap: Record<
  ApprovalAction,
  Record<FaultStateBeforeApproval, FaultStateAfterApproval>
> = {
  approve: {
    PENDING: 'APPROVED',
    RESTAB_PENDING: 'RESTABLISHED',
  },
  reject: {
    PENDING: 'RESTABLISHED',
    RESTAB_PENDING: 'APPROVED',
  },
};

export async function approveOrRejectSelectedPendingFaults(action: ApprovalAction, selectedFaults: { devId: string, faultId: string, state: string }[]) {
  try {
    if (!selectedFaults.length) return;

    if (action !== 'approve' && action !== 'reject') {
      // SHOULD NOT HAPPEN: approval and rejection are the only approval actions allowed
      alert(`Attempted approval action '${action}' is illegal, only possible actions are 'approve' and 'reject'.`);
    }

    for (const fault of selectedFaults) {
      const stateBeforeApproval: FaultStateBeforeApproval = fault.state as FaultStateBeforeApproval;
      if (stateBeforeApproval !== 'PENDING' && stateBeforeApproval !== 'RESTAB_PENDING') {
        // SHOULD NOT HAPPEN: only pending faults can go through approval
        alert(`Attempted approval action '${action}' of fault '${fault.faultId}' for device '${fault.devId}', but fault state is not pending: '${fault.state}'. No action taken.`);
        continue;
      }

      const stateAfterApproval: FaultStateAfterApproval = faultApprovalMap[action][stateBeforeApproval];
      await apiCall('/dac/detected-fault-confirmation', {
        devId: fault.devId,
        faultId: fault.faultId,
        action: stateAfterApproval,
      });
    }

    window.location.reload();
    return;
  } catch (err) {
    console.log(err); toast.error('Houve erro');
  }
}

export default withTransaction('FaultsListDAC', 'component')(FaultsList);
